import { Inject } from 'vue-di-container';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Toaster } from '../../../Services/toast';
import WithRender from './MessageTemplateRenewal.html';
import MessageTemplateRenewalService from './MessageTemplateRenewalService';
import { MessageTemplateRenewalCandidateModel } from './MessageTemplateRenewalModel';
import { TEMPLATE_EXPIRY_EXCEEDS_SENDER_EXPIRY_MESSAGE, translateTemplateRenewalMessage } from './templateRenewalMessages';
import { setPaymentReturnTarget } from '../../../constants/renewalPaymentRedirect';

@WithRender
@Component({
    diProvide: [
        MessageTemplateRenewalService,
    ],
})
export default class MessageTemplateRenewal extends Vue {
    @Inject(MessageTemplateRenewalService) public messageTemplateRenewalService!: MessageTemplateRenewalService;

    @Prop({ type: Boolean, default: false })
    public layoutSup: boolean | any;

    public isLoading: boolean = false;
    public loadFailed: boolean = false;
    public submitInFlight: boolean = false;
    public senderName: string = '';
    public candidates: MessageTemplateRenewalCandidateModel[] = [];
    public selected: MessageTemplateRenewalCandidateModel[] = [];
    public headers: any = [
        { text: this.$t('dashboardPage.templateName'), value: 'templateName', align: 'center', sortable: false },
        { text: this.$t('shared.classification'), value: 'classification', align: 'center', sortable: false },
        {
            text: this.$t('dashboardPage.renewalTemplateType'),
            value: 'isVariableTemplate',
            align: 'center',
            sortable: false,
        },
        {
            text: this.$t('dashboardPage.expiryDate'),
            value: 'currentExpiryDate',
            align: 'center',
            sortable: false,
        },
    ];

    private readonly templateExpiryExceedsSenderExpiryMessage: string = TEMPLATE_EXPIRY_EXCEEDS_SENDER_EXPIRY_MESSAGE;

    public async mounted() {
        this.$emit('childinit', this.layoutSup);
        await this.loadCandidates();
    }

    public get isValidStep(): boolean {
        return this.selected.length > 0;
    }

    public get isSelectionLocked(): boolean {
        return this.getRouteRequestIds().length <= 1;
    }

    public get allEligibleSelected(): boolean {
        return this.candidates.length > 0 && this.selected.length === this.candidates.length;
    }

    public isRowSelectable(item: MessageTemplateRenewalCandidateModel): boolean {
        return !this.isSelectionLocked;
    }

    public isSelected(item: MessageTemplateRenewalCandidateModel): boolean {
        return this.selected.some((s) => s.requestId === item.requestId);
    }

    public toggleRow(item: MessageTemplateRenewalCandidateModel, checked: boolean) {
        if (!this.isRowSelectable(item)) {
            return;
        }
        const others = this.selected.filter((s) => s.requestId !== item.requestId);
        this.selected = checked ? others.concat([item]) : others;
    }

    public toggleAll(checked: boolean) {
        if (this.isSelectionLocked) {
            return;
        }
        this.selected = checked ? this.candidates.slice() : [];
    }

    public async loadCandidates() {
        const requestIds = this.getRouteRequestIds();
        if (!requestIds.length) {
            this.loadFailed = true;
            return;
        }
        this.isLoading = true;
        this.loadFailed = false;
        const response = await this.messageTemplateRenewalService.GetRenewalCandidates(requestIds);
        this.isLoading = false;
        if (!response || !response.isSuccess || !response.result) {
            this.loadFailed = true;
            Toaster.error(this.getErrorText(response && response.errorMessage ? response.errorMessage : ''));
            return;
        }
        this.senderName = response.result.senderName;
        const templates: MessageTemplateRenewalCandidateModel[] = response.result.templates || [];
        this.candidates = templates.filter((c) => c.isEligible);
        const requestIdsSet = new Set(requestIds);
        this.selected = this.candidates.filter((c) => requestIdsSet.has(c.requestId));
        if (templates.some((c) => c.ineligibleReason === this.templateExpiryExceedsSenderExpiryMessage)) {
            Toaster.error(this.$t('shared.templateExpiryExceedsSenderExpiry').toString());
        }
    }

    public cancel() {
        this.$router.push({ name: 'MessageTemplateDashboard' });
    }

    public async payLater() {
        await this.submitRenewal(async () => {
            Toaster.success(this.$t('dashboardPage.templateRenewalRequestAddedSuccessfully').toString());
            await this.$router.push({ name: 'MessageTemplateDashboard' });
        });
    }

    public async payNow() {
        await this.submitRenewal(async (ids: string[]) => {
            setPaymentReturnTarget('messageTemplate');
            await this.$router.push({
                name: 'Payment',
                params: { ids: ids.join(',') },
                query: { fromMessageTemplate: '1' },
            });
        });
    }

    private async submitRenewal(onSuccess: (ids: string[]) => Promise<void>) {
        if (this.submitInFlight || !this.isValidStep) {
            return;
        }
        this.submitInFlight = true;
        try {
            const response = await this.messageTemplateRenewalService.Renew(this.selected.map((s) => s.requestId));
            if (!response || !response.isSuccess || !response.result || !response.result.length) {
                Toaster.error(this.getErrorText(response && response.errorMessage ? response.errorMessage : ''));
                return;
            }
            await onSuccess((response.result as string[]).map((id) => String(id)));
        } finally {
            this.submitInFlight = false;
        }
    }

    private getErrorText(errorMessage: string): string {
        if (errorMessage === '') {
            return this.$t('shared.errorOccurred').toString();
        }
        const translated = this.translateBackendMessage(errorMessage);
        return translated !== null ? translated : errorMessage;
    }

    private translateBackendMessage(message: string): string | null {
        return translateTemplateRenewalMessage(message, (key: string) => this.$t(key).toString());
    }

    private getRouteRequestIds(): string[] {
        const ids = this.$route.query.ids;
        if (typeof ids !== 'string' || !ids.length) {
            return [];
        }
        return ids.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
    }
}
